import type { IndexModelOption } from '@/lib/openrouter-models';
import { authHeaders } from '@/lib/api-auth';

import type {
  ApiKey,
  ApiKeyCreateResponse,
  ChunkRecord,
  IndexNode,
  IndexProgressEvent,
  IndexRequest,
  OrgCiConfig,
  OrgStorageConfig,
  OrgIndexRequest,
  Organization,
  OrgMember,
  PackageAlias,
  PackageSearchResult,
  PackageSummary,
  QueryAnalytics,
  QueryResult,
  RegistryType,
  Workspace,
  WorkspaceImportResult,
  WorkspacePackage,
  WorkspacePackageCandidate,
  IndexMetricsPage,
  PackageIndexHealth,
  PackagePauseKey,
} from './types';

// Build-time public API key. When set, anonymous visitors can ask on public
// packages even when the server still requires auth for retrieval. If
// ALLOW_PUBLIC_API_ACCESS=true on the server, this key is unnecessary for
// browser-side anonymous asks. Note: this value is embedded in the client
// bundle, so it must be a restricted, read-only key.
const PUBLIC_API_KEY = import.meta.env.VITE_PUBLIC_API_KEY as string | undefined;

// ── In-memory cache ─────────────────────────────────────────────────────────

interface CacheEntry<T> {
  data: T;
  expiry: number;
  promise?: undefined;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

function cached<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry && Date.now() < entry.expiry) {
    return Promise.resolve(entry.data);
  }

  // Deduplicate concurrent requests for the same key
  const existing = inflight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, expiry: Date.now() + ttlMs });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

/** Invalidate cache entries matching a prefix (e.g. after mutations). */
export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

// TTL constants
const SHORT_TTL = 30_000; // 30s — for lists that change often (requests)
const MED_TTL = 300_000; // 5m  — for ingest status
const LONG_TTL = 600_000; // 10m — for package list / chunks (rarely change)

async function parseError(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as { error?: string };
    return payload.error ?? `Request failed with status ${response.status}`;
  }

  const text = await response.text();
  return text || `Request failed with status ${response.status}`;
}

export function fetchPackages(): Promise<PackageSummary[]> {
  return cached('packages', LONG_TTL, async () => {
    const response = await fetch('/api/packages');
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json() as Promise<PackageSummary[]>;
  });
}

export function searchPackages(query: string, limit = 24): Promise<PackageSearchResult[]> {
  const normalized = query.trim();
  if (!normalized) return Promise.resolve([]);
  const key = `package-search:${normalized.toLowerCase()}:${limit}`;
  return cached(key, SHORT_TTL, async () => {
    const response = await fetch(
      `/api/packages/search?q=${encodeURIComponent(normalized)}&limit=${limit}`
    );
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json() as Promise<PackageSearchResult[]>;
  });
}

export async function streamPackageIndex(
  input: { source: string; registry: RegistryType; version?: string },
  onEvent: (event: IndexProgressEvent) => void
): Promise<IndexProgressEvent | null> {
  const response = await fetch('/api/index', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  if (!response.body) {
    throw new Error('Streaming response body is not available in this browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lastDoneEvent: IndexProgressEvent | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    let newlineIndex = buffer.indexOf('\n');
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);

      if (line) {
        const event = JSON.parse(line) as IndexProgressEvent;
        onEvent(event);
        if (event.step === 'error') {
          throw new Error(event.message);
        }
        if (event.step === 'done') {
          lastDoneEvent = event;
        }
      }

      newlineIndex = buffer.indexOf('\n');
    }
  }

  const trailing = buffer.trim();
  if (trailing) {
    const event = JSON.parse(trailing) as IndexProgressEvent;
    onEvent(event);
    if (event.step === 'error') {
      throw new Error(event.message);
    }
    if (event.step === 'done') {
      lastDoneEvent = event;
    }
  }

  return lastDoneEvent;
}

export function fetchPackageIndex(
  registry: string,
  name: string,
  version: string
): Promise<IndexNode> {
  const key = `pkg-index:${registry}:${name}:${version}`;
  return cached(key, LONG_TTL, async () => {
    const response = await fetch(
      `/api/packages/${encodeURIComponent(registry)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}/index`
    );
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json() as Promise<IndexNode>;
  });
}

export async function fetchPackageLlmTxt(
  registry: string,
  name: string,
  version: string
): Promise<string> {
  const response = await fetch(
    `/api/packages/${encodeURIComponent(registry)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}/llm-txt`
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.text();
}

export function fetchPackageChunks(
  registry: string,
  name: string,
  version: string
): Promise<ChunkRecord[]> {
  const key = `pkg-chunks:${registry}:${name}:${version}`;
  return cached(key, LONG_TTL, async () => {
    const response = await fetch(
      `/api/packages/${encodeURIComponent(registry)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}/chunks`
    );
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json() as Promise<ChunkRecord[]>;
  });
}

export function fetchIngestStatus(): Promise<Record<string, number>> {
  return cached('ingest-status', MED_TTL, async () => {
    const response = await fetch('/api/ingest-status');
    if (!response.ok) return {};
    return response.json() as Promise<Record<string, number>>;
  });
}

/** Progress event streamed by /api/query while a question runs. */
export interface QueryProgressEvent {
  stage: 'planning' | 'planned' | 'retrieving' | 'retrieved' | 'synthesizing';
  package?: string;
  index?: number;
  total?: number;
  chunkCount?: number;
  subQuestions?: { package?: string; version?: string; question: string }[];
}

export async function queryPackage(input: {
  question: string;
  registry?: string;
  language?: string;
  name: string;
  version: string;
  allowNearby?: boolean;
  answer?: boolean;
  workspaceId?: string | null;
  includeChunks?: boolean;
  apiKey?: string;
  /** Called for each pipeline stage while the server streams progress. */
  onProgress?: (event: QueryProgressEvent) => void;
}): Promise<QueryResult> {
  const {
    registry,
    language,
    name,
    version,
    allowNearby,
    answer,
    workspaceId,
    includeChunks,
    apiKey,
    onProgress,
  } = input;
  if (!registry && !language) throw new Error('Either registry or language is required.');
  // The server omits chunk bodies unless asked. The web UI renders a Sources
  // panel, so default to requesting them; callers that only need the answer
  // can pass includeChunks: false for a much smaller response.
  const body = {
    question: input.question,
    registry,
    language,
    package_name: name,
    version,
    allow_nearby: allowNearby ?? false,
    answer: answer ?? true,
    workspace_id: workspaceId ?? null,
    include_chunks: includeChunks ?? true,
    // Ask for the NDJSON progress stream so the UI can narrate plan →
    // retrieve → synthesize instead of showing a bare spinner.
    stream: true,
  };
  // Resolve auth: explicit apiKey wins, then the logged-in user's token, then a
  // build-time public key so anonymous visitors can still ask on public packages.
  let auth: Record<string, string>;
  if (apiKey) {
    auth = { Authorization: `Bearer ${apiKey}` };
  } else {
    auth = await authHeaders();
    if (!auth.Authorization && PUBLIC_API_KEY) {
      auth = { Authorization: `Bearer ${PUBLIC_API_KEY}` };
    }
  }
  const response = await fetch('/api/query', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...auth,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 202) {
    const pending = (await response.json()) as {
      not_found?: string;
      index_requests?: Array<{ status: string; request_id: string | null }>;
    };
    const request = pending.index_requests?.[0];
    throw new Error(
      `${pending.not_found ?? `${registry}/${name}@${version}`} is not indexed yet. ` +
        (request
          ? `Indexing request ${request.request_id ?? ''} is ${request.status}.`
          : 'An indexing request could not be created.')
    );
  }

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  // The server streams NDJSON: `{"type":"progress",…}` lines while the
  // pipeline runs (plus blank-line heartbeats that keep proxies from reaping
  // the socket), then one `{"type":"result","data":…}` or
  // `{"type":"error",…}` line. A plain single-JSON body (older server, test
  // mocks) is handled by the legacy fallback below.
  let result: QueryResult | undefined;
  let streamError: string | undefined;
  let legacy: (QueryResult & { error?: string }) | undefined;

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      return; // partial or non-JSON line — ignore
    }
    const rec = obj as {
      type?: string;
      event?: QueryProgressEvent;
      data?: QueryResult;
      error?: string;
    } & QueryResult;
    if (rec.type === 'progress' && rec.event) {
      try {
        onProgress?.(rec.event);
      } catch {
        /* progress display must never fail the query */
      }
    } else if (rec.type === 'result') {
      result = rec.data;
    } else if (rec.type === 'error') {
      streamError = rec.error ?? 'Unexpected server error';
    } else if (rec.type === undefined) {
      legacy = rec;
    }
  };

  if (response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIdx = buffer.indexOf('\n');
      while (newlineIdx >= 0) {
        handleLine(buffer.slice(0, newlineIdx));
        buffer = buffer.slice(newlineIdx + 1);
        newlineIdx = buffer.indexOf('\n');
      }
    }
    buffer += decoder.decode();
    handleLine(buffer);
  } else {
    // Environments without a readable body stream: parse the whole payload.
    legacy = (await response.json()) as QueryResult & { error?: string };
  }

  if (streamError) throw new Error(streamError);
  if (result) return result;
  // Legacy single-JSON body: an error-only payload means the pipeline failed
  // mid-stream (a real result always carries `answer`).
  if (legacy) {
    if (legacy.error && legacy.answer === undefined) {
      throw new Error(String(legacy.error));
    }
    return legacy;
  }
  throw new Error('Query stream ended without a result.');
}

// ── Index Requests ──────────────────────────────────────────────────────────

export async function submitIndexRequest(input: {
  registry?: RegistryType;
  name: string;
  version?: string;
  /** Optional explicit docs site URL to crawl; omit to auto-discover from the registry. */
  docsUrl?: string | null;
}): Promise<IndexRequest> {
  const response = await fetch('/api/requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      registry: input.registry,
      name: input.name,
      version: input.version,
      docsUrl: input.docsUrl,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  invalidateCache('index-requests');
  return response.json() as Promise<IndexRequest>;
}

export function fetchIndexRequests(): Promise<IndexRequest[]> {
  return fetch('/api/requests').then(async (response) => {
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json() as Promise<IndexRequest[]>;
  });
}

export async function submitOrgRepoIndexRequest(
  orgId: string,
  input: {
    registry?: 'github' | 'gitlab';
    source_url: string;
    version: string;
    access_token?: string;
    /** Folder inside the repo to index; omit for the whole repository. */
    source_subpath?: string;
    /** Name for the indexed folder. Required alongside `source_subpath`. */
    name?: string;
  },
  token: string
): Promise<OrgIndexRequest> {
  const result = await adminFetch<OrgIndexRequest>(
    `/api/orgs/${encodeURIComponent(orgId)}/repo-requests`,
    'POST',
    token,
    input
  );
  invalidateCache(`org-repo-requests:${orgId}`);
  return result;
}

export function fetchOrgRepoRequests(orgId: string, token: string): Promise<OrgIndexRequest[]> {
  return cached(`org-repo-requests:${orgId}`, SHORT_TTL, () =>
    adminFetch<OrgIndexRequest[]>(
      `/api/orgs/${encodeURIComponent(orgId)}/repo-requests`,
      'GET',
      token
    )
  );
}

export async function restartOrgRepoRequest(
  orgId: string,
  requestId: string,
  token: string
): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/orgs/${encodeURIComponent(orgId)}/repo-requests/${encodeURIComponent(requestId)}/restart`,
    'POST',
    token
  );
  invalidateCache(`org-repo-requests:${orgId}`);
  return result;
}

export async function deleteOrgRepoRequest(
  orgId: string,
  requestId: string,
  token: string
): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/orgs/${encodeURIComponent(orgId)}/repo-requests/${encodeURIComponent(requestId)}`,
    'DELETE',
    token
  );
  invalidateCache(`org-repo-requests:${orgId}`);
  return result;
}

// ── Admin API ────────────────────────────────────────────────────────────────

async function adminFetch<T>(
  path: string,
  method: string,
  token: string,
  body?: unknown
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json() as Promise<T>;
}

export async function adminReindex(
  params: { registry: string; name: string; version: string },
  token: string
): Promise<{ queued: true }> {
  const result = await adminFetch<{ queued: true }>('/api/admin/reindex', 'POST', token, params);
  invalidateCache('packages');
  invalidateCache('pkg-index');
  invalidateCache('pkg-chunks');
  return result;
}

export interface AdminBulkIndexResult {
  submitted: Array<{
    registry: string;
    name: string;
    version: string;
    request_id: string;
    status: string;
  }>;
  skipped: Array<{
    registry?: string;
    name?: string;
    version?: string;
    reason: string;
    lineNumber?: number;
  }>;
  counts: { submitted: number; skipped: number; total: number };
  auto_approve: boolean;
}

export async function adminBulkIndex(
  params: {
    csv?: string;
    packages?: Array<{ registry: string; name: string; version: string }>;
    auto_approve?: boolean;
  },
  token: string
): Promise<AdminBulkIndexResult> {
  const result = await adminFetch<AdminBulkIndexResult>(
    '/api/admin/bulk-index',
    'POST',
    token,
    params
  );
  invalidateCache('packages');
  return result;
}

export async function adminSetHidden(
  params: { registry: string; name: string; version: string; hidden: boolean },
  token: string
): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    '/api/admin/packages/hidden',
    'POST',
    token,
    params
  );
  invalidateCache('packages');
  return result;
}

/**
 * Permanently delete one indexed version. Not reversible: the vectors, chunk
 * mapping and payload objects go with it, and only a re-index brings them back.
 */
export async function adminDeletePackage(
  packageId: string,
  token: string
): Promise<{ ok: true; registry: string; name: string; version: string }> {
  const result = await adminFetch<{ ok: true; registry: string; name: string; version: string }>(
    `/api/admin/packages/${encodeURIComponent(packageId)}`,
    'DELETE',
    token
  );
  invalidateCache('packages');
  invalidateCache('pkg-index');
  invalidateCache('pkg-chunks');
  return result;
}

export async function adminIngest(
  params: { registry: string; name: string; version: string; orgId?: string | null },
  token: string
): Promise<{ ok: true; ingested: number }> {
  const result = await adminFetch<{ ok: true; ingested: number }>(
    '/api/admin/ingest',
    'POST',
    token,
    params
  );
  invalidateCache('ingest-status');
  return result;
}

export async function adminUpdateOverview(
  params: { registry: string; name: string; version: string },
  token: string
): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    '/api/admin/update-overview',
    'POST',
    token,
    params
  );
  invalidateCache('packages');
  return result;
}

export async function adminGetIngestStatus(token: string): Promise<Record<string, number>> {
  return adminFetch('/api/admin/ingest-status', 'GET', token);
}

export async function fetchAdminPackages(token: string): Promise<PackageSummary[]> {
  return adminFetch<PackageSummary[]>('/api/admin/packages', 'GET', token);
}

export async function adminListPackageAliases(
  packageId: string,
  token: string
): Promise<PackageAlias[]> {
  return adminFetch<PackageAlias[]>(
    `/api/admin/package-aliases?packageId=${encodeURIComponent(packageId)}`,
    'GET',
    token
  );
}

export async function adminCreatePackageAlias(
  params: {
    registry: string;
    name: string;
    version: string;
    packageId: string;
    sourceSubpath?: string | null;
    resolvedCommitSha?: string | null;
    relationType?: string;
    verificationMethod?: string | null;
  },
  token: string
): Promise<PackageAlias> {
  const result = await adminFetch<PackageAlias>(
    '/api/admin/package-aliases',
    'POST',
    token,
    params
  );
  invalidateCache('packages');
  return result;
}

export async function adminUpdatePackageAlias(
  id: string,
  params: {
    registry: string;
    name: string;
    version: string;
    packageId: string;
    sourceSubpath?: string | null;
    resolvedCommitSha?: string | null;
    relationType?: string;
    verificationMethod?: string | null;
  },
  token: string
): Promise<PackageAlias> {
  const result = await adminFetch<PackageAlias>(
    `/api/admin/package-aliases/${encodeURIComponent(id)}`,
    'PATCH',
    token,
    params
  );
  invalidateCache('packages');
  return result;
}

export async function adminDeletePackageAlias(id: string, token: string): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/admin/package-aliases/${encodeURIComponent(id)}`,
    'DELETE',
    token
  );
  invalidateCache('packages');
  return result;
}

export interface AdminResolveAliasesResponse {
  packageId: string;
  registry: string;
  name: string;
  version: string;
  sourceUrl: string;
  removed: number;
  written: number;
  stats: {
    manifestsFound: number;
    candidatesParsed: number;
    candidatesDeduped: number;
  };
  verified: Array<{
    registry: string;
    name: string;
    version: string | null;
    manifestPath: string;
    verificationMethod: 'tarball-manifest-match' | 'llm-search';
    note: string | null;
  }>;
  skipped: Array<{
    registry: string;
    name: string;
    version: string | null;
    manifestPath: string;
    status: string;
    note: string | null;
  }>;
}

export async function adminResolveAliases(
  packageId: string,
  opts: { useLlmFallback?: boolean } | undefined,
  token: string
): Promise<AdminResolveAliasesResponse> {
  const result = await adminFetch<AdminResolveAliasesResponse>(
    `/api/admin/packages/${encodeURIComponent(packageId)}/resolve-aliases`,
    'POST',
    token,
    opts ?? {}
  );
  invalidateCache('packages');
  return result;
}

export async function adminRestartRequest(id: string, token: string): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/admin/requests/${encodeURIComponent(id)}/restart`,
    'POST',
    token
  );
  invalidateCache('index-requests');
  return result;
}

export async function adminCancelRequest(id: string, token: string): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/admin/requests/${encodeURIComponent(id)}/cancel`,
    'POST',
    token
  );
  invalidateCache('index-requests');
  return result;
}

export async function adminApproveRequest(
  id: string,
  token: string,
  alias?: { registry: string; name: string; version: string } | null,
  source?: { registry: string; name: string; version: string } | null,
  sourceSubpath?: string | null
): Promise<{ ok: true }> {
  const body: Record<string, string> = {};
  if (alias) {
    body.alias_registry = alias.registry;
    body.alias_name = alias.name;
    body.alias_version = alias.version;
  }
  if (source) {
    body.source_registry = source.registry;
    body.source_name = source.name;
    body.source_version = source.version;
  }
  if (sourceSubpath?.trim()) {
    body.source_subpath = sourceSubpath.trim();
  }
  const result = await adminFetch<{ ok: true }>(
    `/api/admin/requests/${encodeURIComponent(id)}/approve`,
    'POST',
    token,
    Object.keys(body).length > 0 ? body : undefined
  );
  invalidateCache('index-requests');
  return result;
}

export interface BulkApproveResult {
  approved: number;
  approved_ids: string[];
  failed: Array<{ id: string; error: string }>;
}

export async function adminBulkApproveRequests(
  ids: string[],
  token: string
): Promise<BulkApproveResult> {
  const result = await adminFetch<BulkApproveResult>(
    '/api/admin/requests/bulk-approve',
    'POST',
    token,
    { ids }
  );
  invalidateCache('index-requests');
  return result;
}

export interface PackagePopularity {
  registry: string;
  name: string;
  monthlyDownloads: number | null;
  totalDownloads?: number | null;
  likes?: number | null;
  unavailableReason?: string;
}

export async function adminFetchPackagePopularity(
  packages: Array<{ registry: string; name: string }>,
  token: string
): Promise<PackagePopularity[]> {
  const result = await adminFetch<{ results: PackagePopularity[] }>(
    '/api/admin/package-popularity',
    'POST',
    token,
    { packages }
  );
  return result.results ?? [];
}

export async function adminDeleteRequest(id: string, token: string): Promise<{ ok: true }> {
  const result = await adminFetch<{ ok: true }>(
    `/api/admin/requests/${encodeURIComponent(id)}`,
    'DELETE',
    token
  );
  invalidateCache('index-requests');
  return result;
}

// ── Current user info ────────────────────────────────────────────────────────

export async function fetchMe(
  token: string
): Promise<{ id?: string; email: string; isAdmin: boolean }> {
  return adminFetch('/api/me', 'GET', token);
}

// ── Organization API ─────────────────────────────────────────────────────────

export async function fetchOrganizations(token: string): Promise<Organization[]> {
  return adminFetch('/api/orgs', 'GET', token);
}

export function fetchOrgPackages(orgId: string, token: string): Promise<PackageSummary[]> {
  const key = `org-packages:${orgId}`;
  return cached(key, MED_TTL, () =>
    adminFetch<PackageSummary[]>(`/api/orgs/${encodeURIComponent(orgId)}/packages`, 'GET', token)
  );
}

export async function setOrgPackageAlias(
  orgId: string,
  packageId: string,
  alias: string | null,
  token: string
): Promise<{ alias: string | null }> {
  const result = await adminFetch<{ alias: string | null }>(
    `/api/orgs/${encodeURIComponent(orgId)}/packages/${encodeURIComponent(packageId)}/alias`,
    'PATCH',
    token,
    { alias }
  );
  invalidateCache(`org-packages:${orgId}`);
  invalidateCache('packages');
  return result;
}

export async function setOrgPackageArchived(
  orgId: string,
  packageId: string,
  archived: boolean,
  token: string
): Promise<{ affected: number }> {
  const result = await adminFetch<{ affected: number }>(
    `/api/orgs/${encodeURIComponent(orgId)}/packages/${encodeURIComponent(packageId)}/archive`,
    'PATCH',
    token,
    { archived }
  );
  invalidateCache(`org-packages:${orgId}`);
  invalidateCache('packages');
  return result;
}

export async function createOrganization(
  input: { name: string; slug: string },
  token: string
): Promise<Organization> {
  return adminFetch('/api/orgs', 'POST', token, input);
}

export async function fetchOrgMembers(orgId: string, token: string): Promise<OrgMember[]> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/members`, 'GET', token);
}

export async function addOrgMember(
  orgId: string,
  input: { user_id?: string; email?: string; role?: 'admin' | 'member' },
  token: string
): Promise<OrgMember> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/members`, 'POST', token, input);
}

export async function removeOrgMember(
  orgId: string,
  userId: string,
  token: string
): Promise<{ ok: true }> {
  return adminFetch(
    `/api/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
    'DELETE',
    token
  );
}

export async function fetchApiKeys(orgId: string, token: string): Promise<ApiKey[]> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/keys`, 'GET', token);
}

export async function fetchOrgStorage(orgId: string, token: string): Promise<OrgStorageConfig> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/storage`, 'GET', token);
}

/**
 * Save an org's storage config. `secretAccessKey` is omitted when blank so the
 * stored credential survives an edit to the bucket or region.
 */
export async function updateOrgStorage(
  orgId: string,
  input: {
    bucket: string;
    region: string;
    endpoint?: string | null;
    accessKeyId: string;
    secretAccessKey?: string | null;
    forcePathStyle?: boolean;
    enabled?: boolean;
  },
  token: string
): Promise<OrgStorageConfig> {
  const body: Record<string, unknown> = {
    bucket: input.bucket,
    region: input.region,
    endpoint: input.endpoint ?? null,
    access_key_id: input.accessKeyId,
    force_path_style: input.forcePathStyle ?? false,
  };
  if (input.secretAccessKey) body.secret_access_key = input.secretAccessKey;
  if (input.enabled !== undefined) body.enabled = input.enabled;
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/storage`, 'PUT', token, body);
}

export async function deleteOrgStorage(orgId: string, token: string): Promise<{ ok: true }> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/storage`, 'DELETE', token);
}

/** OpenRouter models an org can index with, and the instance's own index model. */
export async function fetchCiModels(
  orgId: string,
  token: string
): Promise<{ models: IndexModelOption[]; recommended: string }> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/ci-models`, 'GET', token);
}

export async function fetchCiConfig(orgId: string, token: string): Promise<OrgCiConfig> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/ci-config`, 'GET', token);
}

/**
 * Update an org's CI config.
 *
 * `llmApiKey` is three-state on purpose: omit it to leave the stored key
 * untouched (so toggling `ciEnabled` doesn't require re-entering the secret),
 * pass `null` to clear it, pass a string to replace it. The key is never
 * returned by a read — only whether one exists and its last four characters.
 */
export async function updateCiConfig(
  orgId: string,
  input: {
    llmApiKey?: string | null;
    llmIndexModel?: string | null;
    ciEnabled?: boolean;
    ciNamePrefix?: string | null;
    ciAllowPublicShadow?: boolean;
    ciRetainVersions?: number | null;
  },
  token: string
): Promise<OrgCiConfig> {
  const body: Record<string, unknown> = {};
  if ('llmApiKey' in input) body.llm_api_key = input.llmApiKey;
  if (input.llmIndexModel !== undefined) body.llm_index_model = input.llmIndexModel;
  if (input.ciEnabled !== undefined) body.ci_enabled = input.ciEnabled;
  if ('ciNamePrefix' in input) body.ci_name_prefix = input.ciNamePrefix;
  if (input.ciAllowPublicShadow !== undefined)
    body.ci_allow_public_shadow = input.ciAllowPublicShadow;
  if ('ciRetainVersions' in input) body.ci_retain_versions = input.ciRetainVersions;
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/ci-config`, 'PUT', token, body);
}

export async function fetchWorkspaces(orgId: string, token: string): Promise<Workspace[]> {
  return cached(`workspaces:${orgId}`, MED_TTL, () =>
    adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/workspaces`, 'GET', token)
  );
}

export async function createWorkspace(
  orgId: string,
  input: { name: string; slug?: string; description?: string },
  token: string
): Promise<Workspace> {
  const result = await adminFetch<Workspace>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces`,
    'POST',
    token,
    input
  );
  invalidateCache(`workspaces:${orgId}`);
  invalidateCache(`workspace-packages:${orgId}:`);
  return result;
}

export async function fetchWorkspacePackages(
  orgId: string,
  workspace: string,
  token: string
): Promise<WorkspacePackage[]> {
  return cached(`workspace-packages:${orgId}:${workspace}`, MED_TTL, () =>
    adminFetch(
      `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/packages`,
      'GET',
      token
    )
  );
}

export async function updateWorkspacePackageVersion(
  orgId: string,
  workspace: string,
  workspacePackageId: string,
  input: { version_spec: string | null },
  token: string
): Promise<WorkspacePackage> {
  const result = await adminFetch<WorkspacePackage>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/packages/${encodeURIComponent(workspacePackageId)}`,
    'PATCH',
    token,
    input
  );
  invalidateCache(`workspace-packages:${orgId}:${workspace}`);
  invalidateCache(`workspaces:${orgId}`);
  return result;
}

export async function importWorkspaceManifest(
  orgId: string,
  workspace: string,
  input: {
    file_name: string;
    raw_content: string;
    lock_file_name?: string;
    lock_raw_content?: string;
  },
  token: string
): Promise<WorkspaceImportResult> {
  const result = await adminFetch<WorkspaceImportResult>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/import`,
    'POST',
    token,
    input
  );
  invalidateCache(`workspaces:${orgId}`);
  invalidateCache(`workspace-packages:${orgId}:${workspace}`);
  return result;
}

export async function recheckWorkspacePackages(
  orgId: string,
  workspace: string,
  token: string
): Promise<{ resolvedCount: number; totalCount: number }> {
  const result = await adminFetch<{ resolvedCount: number; totalCount: number }>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/recheck`,
    'POST',
    token
  );
  invalidateCache(`workspace-packages:${orgId}:${workspace}`);
  return result;
}

export async function fetchWorkspacePackageCandidates(
  orgId: string,
  workspace: string,
  workspacePackageId: string,
  token: string
): Promise<WorkspacePackageCandidate[]> {
  return adminFetch<WorkspacePackageCandidate[]>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/packages/${encodeURIComponent(workspacePackageId)}/candidates`,
    'GET',
    token
  );
}

export async function assignWorkspacePackage(
  orgId: string,
  workspace: string,
  workspacePackageId: string,
  packageId: string | null,
  token: string
): Promise<WorkspacePackage> {
  const result = await adminFetch<WorkspacePackage>(
    `/api/orgs/${encodeURIComponent(orgId)}/workspaces/${encodeURIComponent(workspace)}/packages/${encodeURIComponent(workspacePackageId)}/assign`,
    'POST',
    token,
    { package_id: packageId }
  );
  invalidateCache(`workspace-packages:${orgId}:${workspace}`);
  return result;
}

export async function createApiKey(
  orgId: string,
  input: { name?: string; scopes?: string[]; expires_at?: string },
  token: string
): Promise<ApiKeyCreateResponse> {
  return adminFetch(`/api/orgs/${encodeURIComponent(orgId)}/keys`, 'POST', token, input);
}

export async function revokeApiKey(
  orgId: string,
  keyId: string,
  token: string
): Promise<{ ok: true }> {
  return adminFetch(
    `/api/orgs/${encodeURIComponent(orgId)}/keys/${encodeURIComponent(keyId)}`,
    'DELETE',
    token
  );
}

// ── Query feedback ──────────────────────────────────────────────────────────

export async function submitQueryFeedback(queryLogId: string, feedback: 1 | -1): Promise<void> {
  const response = await fetch(`/api/query-log/${encodeURIComponent(queryLogId)}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback }),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
}

// ── Admin package pauses ────────────────────────────────────────────────────

/** Paused packages and ones on a failure streak, paused first. */
export async function fetchPackageHealth(token: string): Promise<PackageIndexHealth[]> {
  return adminFetch('/api/admin/package-health', 'GET', token);
}

/**
 * Stop automatic indexing of a package (version-watch, auto-index on a query
 * miss, the stall sweep) and its queued and running requests. Explicit admin
 * reindex/approve/restart still run.
 */
export async function adminPausePackage(
  params: PackagePauseKey & { reason?: string },
  token: string
): Promise<{ paused: true; failed: number; stopped: number }> {
  return adminFetch('/api/admin/packages/pause', 'POST', token, params);
}

/** Turn automatic indexing back on and clear the failure streak. */
export async function adminResumePackage(
  params: PackagePauseKey,
  token: string
): Promise<{ resumed: boolean }> {
  return adminFetch('/api/admin/packages/resume', 'POST', token, params);
}

// ── Admin index metrics ─────────────────────────────────────────────────────

export async function fetchIndexMetrics(
  token: string,
  opts: { limit?: number; offset?: number; query?: string; packageId?: string } = {}
): Promise<IndexMetricsPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? 50),
    offset: String(opts.offset ?? 0),
  });
  if (opts.query) params.set('q', opts.query);
  if (opts.packageId) params.set('packageId', opts.packageId);
  return adminFetch(`/api/admin/index-metrics?${params}`, 'GET', token);
}

// ── Admin analytics ─────────────────────────────────────────────────────────

export async function fetchQueryAnalytics(
  token: string,
  days: 7 | 14 | 30
): Promise<QueryAnalytics> {
  return adminFetch(`/api/admin/analytics?days=${days}`, 'GET', token);
}
